import * as React from "react";
import { ComboBox } from "@fluentui/react";
import TextDataInput from "../../../repertoire/components/textDataInput/TextDataInput";
import { SaveAndClose } from "./SaveAndClose";
import { ITriggerManualJob } from "../../../redux/types/ITriggerManualJob";

export interface IGeneratePaymentDetailsParametersProps {
    createNewJob: () => any;
    triggerManualJob: (params: any) => any;
    manualJobParameters: ITriggerManualJob;
    scheduledJobSet?: (value: boolean) => void;
    onError?: (errorMessage: string) => void;
    formControls: any[];
    enableMemberNumber?: boolean;
}

const GeneratePaymentDetailsParameters = (props: IGeneratePaymentDetailsParametersProps) => {
    const [data, setData] = React.useState<any[]>([]);
    const [generalError, setGeneralError] = React.useState<string[]>([]);

    React.useEffect(() => {
        if (!props.formControls) return;

        const initialControls = props.formControls.map(item => ({ ...item }));

        const doUndoOptions = [
            { key: "Do", text: "Generate Payment Details" },
            { key: "Undo", text: "Undo Generation of Payment Details" }
        ];

        let doUndoItem = initialControls.find(i => i.Caption === "Do/Undo Generation");
        if (doUndoItem) {
            doUndoItem.Options = doUndoOptions;
            doUndoItem.CurrentValue = doUndoItem.CurrentValue || "";
        } else {
            initialControls.push({
                Caption: "Do/Undo Generation",
                Type: "combobox",
                CurrentValue: "",
                Options: doUndoOptions
            });
        }

        const jobTypeOptions = [
            { key: "", text: "" },
            { key: "STANDARD", text: "Standard Payment Table Generation" },
            { key: "USAGE", text: "Usage Level Payment Table Generation" }
        ];

        let jobTypeItem = initialControls.find(i => i.Caption === "Payment Job Type");
        if (jobTypeItem) {
            jobTypeItem.Options = jobTypeOptions;
            jobTypeItem.CurrentValue = jobTypeItem.CurrentValue || "";
        } else {
            initialControls.push({
                Caption: "Payment Job Type",
                Type: "combobox",
                CurrentValue: "",
                Options: jobTypeOptions,
                ControlID: "GPD_JobType"
            });
        }

        let paymentRunItem = initialControls.find(i => i.Caption === "Payment Run");
        if (!paymentRunItem) {
            initialControls.push({
                Caption: "Payment Run",
                Type: "combobox",
                CurrentValue: "",
                Options: []
            });
        }

        setData(initialControls);
    }, [props.formControls]);

    const handleChange = (formItem: any, value?: any) => {
        setData(prevState =>
            prevState.map(item =>
                item.Caption === formItem.Caption
                    ? { ...item, CurrentValue: value?.key ?? value }
                    : item
            )
        );
    };

    const validateFields = (): string[] => {
        const paymentRun = data.find(i => i.Caption === "Payment Run");
        const doUndo = data.find(i => i.Caption === "Do/Undo Generation");
        const errors: string[] = [];
        if (!paymentRun?.CurrentValue || (Array.isArray(paymentRun.CurrentValue) && paymentRun.CurrentValue.length === 0)) {
            errors.push("Payment Run is required");
        }
        if (!doUndo?.CurrentValue) errors.push("Do/Undo Generation is required");
        setGeneralError(errors);
        return errors;
    };
    
    const handleTriggerManualJob = async () => {
        const validationErrors = validateFields();
        if (validationErrors.length > 0) return false;
        
        try {
            const jobType = data.find(i => i.Caption === "Payment Job Type");
            let updatedData = data;
            if (!jobType?.CurrentValue || jobType.CurrentValue === "") 
            {
                updatedData = data.map(item =>
                    item.Caption === "Payment Job Type"
                        ? { ...item, CurrentValue: "STANDARD" }
                        : item
                );
            }
            await props.triggerManualJob(updatedData);
            props.scheduledJobSet?.(true);
            return true;
        } catch (err: any) {
            props.onError?.(err?.message || err?.toString());
            return false;
        }
    };

    const getFormComponents = () =>
        data.map(field => {
            if (field.Type === "combobox") {
                let options: { key: string; text: string }[] = [];

                if (field.Caption === "Payment Run") {
                    options =
                        field.DataSourceRepo?.paymentRuns?.map((r: any) => ({
                            key: r.code,
                            text: r.description
                        })) || [];
                } else {
                    options = Array.isArray(field.Options) ? field.Options : [];
                }

                const calloutProps =
                    field.Caption === "Payment Run" ? { calloutMaxHeight: 270 } : undefined;

                return (
                    <div className="form-group col-md-6 col-sm-6 col-xs-12" key={field.Caption}>
                        <ComboBox
                            label={field.Caption}
                            selectedKey={field.CurrentValue}
                            options={options}
                            onChange={(e, option) => handleChange(field, option)}
                            useComboBoxAsMenuWidth
                            allowFreeform={false}
                            calloutProps={calloutProps}
                        />
                    </div>
                );
            }

            if (field.Type === "text-input") {
                return (
                    <div className="form-group col-md-6 col-sm-6 col-xs-12" key={field.Caption}>
                        <TextDataInput
                            label={field.Caption}
                            fieldName={field.Caption}
                            value={field.CurrentValue}
                            changeData={text => handleChange(field, text)}
                            isHidden={false}
                            isMandatory={false}
                            readOnly={field.Disabled}
                        />
                    </div>
                );
            }

            return null;
        });

    return (
        <div className="form-group col-md-12 col-sm-12 col-xs-12">
            {generalError.length > 0 && (
                <div className="alert alert-danger">
                    {generalError.join(", ")}
                </div>
            )}

            <div className="row">{getFormComponents()}</div>

            <div className="row">
                <SaveAndClose
                    manualJobParameters={props.manualJobParameters}
                    triggerManualJob={handleTriggerManualJob}
                    createNewJob={props.createNewJob}
                    scheduledJobSet={props.scheduledJobSet}
                    onError={props.onError}
                />
            </div>
        </div>
    );
};

export default GeneratePaymentDetailsParameters;
